/**
 * Campaign — one event's paid promotion: the operator's brief, the proposal
 * composed against this event's own catalogue, the ad tree Meta runs, and the
 * spend it reports back.
 *
 * Path: `workspaces/{orgId}/events/{eventId}/campaigns/{campaignId}` — a
 * campaign promotes exactly one event and is stored under it, so containment
 * is the whole declaration of ownership and no `eventId` field stands beside
 * it.
 *
 * Policy: `tickets.ads.manage` + `mfa: true`, one token for the whole surface
 * (the catalog's own amendment note states what earned it). `read` and
 * `list` are declared SEPARATELY against that same predicate: Firestore's own
 * `allow read` line is shorthand for get+list, so the emitter already folds
 * this read grant into list eligibility with no rule of its own, but the
 * kernel's `me.can('list', …)` consults only rules whose action list carries
 * `list` and does no such promotion.
 *
 * ── THE TWO UPDATE MASKS, AND WHY THEY CANNOT WIDEN EACH OTHER ─────────────
 * The operator writes the brief while the campaign is a `draft`
 * (`['brief']`), and the parked proposal plus its amount while it is
 * `proposed` (`['proposal', 'budgetMinor']`). Every other field is
 * server-only by the masks' silence — including `status` itself, so no client
 * write moves this document between states at all.
 *
 * Firestore ORs every applicable `allow update` clause, so two masks on one
 * document are only two masks while no write satisfies both. Each carries its
 * own `.from(...)` pre-state against the SAME field compared to DISTINCT
 * literals — `draft` and `proposed` — so a stored campaign satisfies at most
 * one of them, and a write that names keys from both fails each one's
 * `hasOnly`. From `building` onwards neither pre-state holds and the document
 * closes to the client entirely, which is what makes the creative bytes
 * frozen while the build re-uploads them.
 *
 * `brief` and `proposal` are maps, and `affectedKeys()` reports TOP-LEVEL keys
 * only: masking a map in admits every mutation INSIDE it. So neither map may
 * carry a server-written key — every server fact the proposal is composed
 * against (`proposedAt`, `priceFloorCents`, `endsAt`, `currency`) is a
 * top-level field the masks do not name, and `proposal` carries the operator's
 * five controls and nothing else: the picture, the headline, the body, the
 * radius and the minimum age, where headline and body each carry their own
 * additive per-locale map beside the source string.
 *
 * What no rule can carry, and the launch callable therefore owns: the mask
 * sees the key `proposal` and nothing inside it, so a client may write
 * `proposal: null` over a composed one, drop a key, or name an `assetId`
 * belonging to no asset of this campaign. The map's shape, and that id's
 * resolution against this campaign's own assets, are re-checked where the
 * money commits, because that is the only place left that can.
 *
 * Two of the operator's controls and one of Meta's own fields carry a
 * constraint their `.describe()` deliberately does not: `radiusKm` and
 * `minAge` are stored exactly as the operator states them and clamped by the
 * builder against the range Meta's API accepts at the time it posts, never
 * against a range transcribed here; and `metaStatus`, `learningStage` and
 * `attributionWindow` stay strings because their value sets are Meta's to
 * publish, and an enum here would assert a set this repo does not own.
 *
 * ── WHY `workspaceId` IS SERVER-WRITTEN ───────────────────────────────────
 * The overview's own binding below matches `{path=**}/campaigns/{campaignId}`,
 * and a recursive wildcard matches ZERO or more segments — so it also matches
 * a campaign at its real nested path, and Firestore ORs every applicable allow
 * expression. The per-event list is therefore governed by both lines at once:
 * the path-keyed grant OR the field-keyed one. A document whose `workspaceId`
 * disagreed with the workspace in its own path would be readable, through that
 * OR, by a token holder in the workspace the FIELD names and not the one the
 * PATH names.
 *
 * So the disagreement is made unrepresentable rather than merely unlikely: the
 * create pattern pins the field to `null` alongside every other server-owned
 * field, and no update mask names it, so no client write can ever author it.
 * A server entry stamps it from the path it is writing to. The comparison that
 * would state the invariant directly — the field against `pathVar('orgId')` —
 * cannot be written here: `pathVar` resolves to `undefined` in the kernel, so
 * a rule carrying it answers `me.can('create', …)` false for every actor and
 * blinds the gate the FE reads.
 *
 * The overview consequently sees a campaign only once the server has stamped
 * it, which is the state the overview cares about: a draft nobody has proposed
 * belongs to no band it renders.
 *
 * ── THE CONFIRMATION ──────────────────────────────────────────────────────
 * `launch` keeps its whole authority predicate as a kernel rule — it is what
 * the confirm button gates on and what the callable re-checks — and
 * `.requiresHandler` is what keeps it out of the emitted rules, so NO client
 * write reaches `building`. The build trigger fires on that transition and
 * spends money, and the settlement only the callable can perform — a
 * proposal parked until the world moved, a price floor that has since
 * changed, an event-scoped grant no rule can see, since rules resolve a
 * permission at the workspace and never at the event — is binding exactly
 * while it is the only path there. A second, client-reachable path would
 * leave every one of those checks advisory.
 *
 * The predicate names the state it moves out of, and the kernel evaluates it
 * against whatever document it is handed — so a caller who assembles a shape
 * saying `proposed` is answered `true`, and this predicate guarantees nothing
 * about a document it never saw. What closes that is the wire contract: the
 * callable's input carries the campaign's id and no part of its body
 * (`launchCampaign.action.ts`), so the shape cannot cross the wire, and the
 * callable re-reads the document inside the transaction that moves it. The
 * obligation is the callable's, not the predicate's.
 *
 * ── WHAT IS NOT DECLARED ──────────────────────────────────────────────────
 * `delete` (no client may erase a campaign, and no rule grants it: the only
 * path that removes one is the event's own delete door, whose refuse-or-sweep
 * split `NO_SPEND_STATUSES` above draws and whose refusal counts through
 * `dependents.const.ts`'s `spendingCampaigns` row), the
 * `draft → drafting → proposed` composition, the stop/pause/resume writes,
 * and every field the build and the poller own. Each falls to the generated
 * default-deny tail; the Admin SDK, which rules do not gate, is their only
 * writer.
 *
 * ── ONE FLAT SHAPE, NOT A UNION ───────────────────────────────────────────
 * `status` decides which other fields are populated, which ordinarily makes a
 * schema a discriminated union. This one stays a flat enum beside a bag of
 * nullish fields because the rules are what read it: the recording proxy walks
 * `keyof z.infer<Schema>` and cannot descend into a union's variants, and both
 * masks and both pre-states are expressed as `data.status` at the top level.
 * The price a flat shape would carry — `status: 'live'` beside
 * `budgetMinor: null` — is refused by the schema's own refine instead, which
 * is where the union's narrowing would otherwise have been doing the work.
 */

import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { LocalizedTextSchema } from "@momo/i18n";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { CampaignFailureReasonSchema } from "./campaign-failure.const.ts";
import { Cents } from "./ticket-type.entity.ts";
import { type Actor, Id, MetaAdvertiserSchema } from "./workspace.entity.ts";

/** The length an operator's own words are capped at before they reach the copy
 *  composition and, through it, Meta — a token holder may write these, and an
 *  uncapped one is a megabyte of prompt on a paid path. */
export const CAMPAIGN_TEXT_MAX_LENGTH = 2000;

// Money at the ad network, in the ad account's own minor unit — never `Cents`,
// whose own describe names euro cents: `currency` below is what says which
// unit these integers count, and an ad account is free to bill in another.
const MinorUnits = z.number().int().min(0);

// Ours, never Meta's. `submitted`, `live`, `rejected` and `ended` are written
// by the poller reading `effective_status`; `paused` is written by the
// operator's own stop. Meta's own tokens stay in `metaStatus` below, so no
// value here has to be kept in step with a vocabulary we do not own.
// The states a campaign reaches only through the confirmation, and the states
// in which no proposal exists yet. A status added to the enum joins neither by
// default, so it inherits the proposal obligation and owes a deliberate
// decision about the confirmation's own facts.
const LAUNCHED_STATUSES: ReadonlyArray<CampaignStatus> = [
	"building",
	"submitted",
	"live",
	"paused",
	// A rejection is something Meta says about an ad it already has, so it is
	// reached only from the far side of the confirmation — unlike `failed`,
	// which a composition can reach before any money is committed and which
	// therefore sits with the states owing no proposal.
	"rejected",
	"ended",
];
const PRE_PROPOSAL_STATUSES: ReadonlyArray<CampaignStatus> = [
	"draft",
	"drafting",
	"failed",
];

export const CampaignStatusSchema = z
	.enum([
		"draft",
		"drafting",
		"proposed",
		"building",
		"submitted",
		"live",
		"paused",
		"rejected",
		"ended",
		"failed",
	])
	.describe(
		"where this promotion stands, in our own words — draft before anything is composed, drafting while it is, proposed once the operator has something to answer, building from the confirmation until the tree exists at Meta, then whatever the poller reads back",
	);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

/**
 * The statuses in which no delivery this product started can still draw on
 * the ad account: nothing has been built at Meta yet (`draft`, `drafting`,
 * `proposed`), the build was answered before it ever activated an ad
 * (`failed`), Meta refused to deliver it (`rejected`), or the run is archived
 * (`ended`). A build that died AFTER activating an ad leaves the document at
 * `building`, never here — ./reconcile.schedule.ts's own reasoning, and the
 * reason `failed` belongs on this side of the line.
 *
 * Its COMPLEMENT over `CampaignStatusSchema.options` is where money is
 * committed at Meta, and that is the set the event's delete door refuses on
 * (`dependents.const.ts`'s `spendingCampaigns`) while sweeping everything
 * named here. The complement is derived there rather than re-typed, so a
 * status added to the enum refuses a delete by construction instead of
 * slipping into a cascade that would destroy the only record of a live ad —
 * the same relation `checkout.entity.ts`'s `TERMINAL_STATUSES` carries for
 * that door's checkout probe, in the safe direction.
 */
export const NO_SPEND_STATUSES: ReadonlyArray<CampaignStatus> = [
	"draft",
	"drafting",
	"proposed",
	"rejected",
	"ended",
	"failed",
];

// What the operator writes before a proposal exists. Its keys are ALL
// operator-writable by construction — the `['brief']` mask closes the
// document's own key set and says nothing about this map's sub-keys — so
// nothing the server owns may join it.
//
// The amount is deliberately absent: money on this document is one top-level
// field (`budgetMinor`) where a mask can see it, and the operator's chosen
// amount reaches the server as the propose callable's own input rather than
// as a second copy of the figure the ad set will carry.
export const CampaignBriefSchema = z.object({
	notes: z
		.string()
		.max(CAMPAIGN_TEXT_MAX_LENGTH)
		.nullish()
		.describe(
			"the operator's own words about what this ad should say — the one input to the copy composition that the event's own catalogue cannot supply",
		),
});
export type CampaignBrief = z.infer<typeof CampaignBriefSchema>;

// The five controls the proposal screen gives the operator — picture,
// headline, body, radius, minimum age — and nothing else. Every key here is
// client-writable while the campaign is `proposed` (see the mask note in the
// file header), so a server-composed fact that must survive the operator's
// hands is a top-level field instead.
export const CampaignProposalSchema = z.object({
	assetId: Id.describe(
		"which creative runs — names one of this campaign's own assets; the picture control writes nothing but this choice",
	),
	headline: z
		.string()
		.min(1)
		.max(CAMPAIGN_TEXT_MAX_LENGTH)
		.describe("the ad's own headline, in the deployment's own language"),
	headlineByLocale: LocalizedTextSchema.nullish().describe(
		"the headline's additive per-locale map — one ad per locale runs; an edit in a language that is not the source moves only that entry",
	),
	body: z
		.string()
		.min(1)
		.max(CAMPAIGN_TEXT_MAX_LENGTH)
		.describe("the ad's body copy, in the deployment's own language"),
	bodyByLocale: LocalizedTextSchema.nullish().describe(
		"the body's additive per-locale map, the headline's own pair-shape",
	),
	radiusKm: z
		.number()
		.int()
		.positive()
		.describe("how far around the venue the ad reaches, in kilometres"),
	minAge: z
		.number()
		.int()
		.positive()
		.describe("the youngest audience this ad may reach, in years"),
});
export type CampaignProposal = z.infer<typeof CampaignProposalSchema>;

// An id Meta minted and handed back, never one this model issues — so it is
// not the shared `Id` vocabulary, which names documents this deployment
// stores. Non-emptiness is the whole constraint a value read straight out of
// a Graph response can carry: Meta is authoritative for the shape of its own
// ids, and a format asserted here could only ever reject what it just said.
const MetaObjectId = z.string().min(1);

// The ids the build writes back, each before the next POST fires — so a
// re-entered build hydrates what exists and skips it. Every one nullish: a
// partially built tree is the ordinary mid-flight shape, not a defect.
export const CampaignMetaIdsSchema = z.object({
	campaign: MetaObjectId.nullish(),
	adSet: MetaObjectId.nullish(),
	creative: MetaObjectId.nullish(),
	ad: MetaObjectId.nullish(),
});
export type CampaignMetaIds = z.infer<typeof CampaignMetaIdsSchema>;

/** A count of things that happened — never negative, never fractional. */
const Count = z.number().int().min(0);

/**
 * What META says this campaign delivered, read in one call and written in one
 * write, so no surface can divide a fresh spend by a stale click count.
 *
 * Every figure here is the ad network's own claim about its own delivery, and
 * this product can corroborate none of it: that is exactly why it is kept
 * apart from `observed` below rather than summed into one set of numbers. The
 * two are shown side by side and the reader is told which is which.
 *
 * Fields justified:
 *   reach       — people, not impressions; the figure an operator means by
 *                 "how many did it get in front of"
 *   impressions — times shown, which exceeds reach whenever anyone saw it
 *                 twice; the pair is what makes frequency legible without
 *                 storing a third number derived from these two
 *   clicks      — read by the traffic verdict (`campaignTraffic` below): it is
 *                 the ONLY independent evidence that anyone was sent here at
 *                 all, and without it "nobody arrived" and "everyone who
 *                 arrived was untraceable" are the same reading
 *   actions     — Meta's `actions` array under META'S OWN `action_type` keys,
 *                 carried whole. This tree transcribes only the four tokens it
 *                 branches on; a purchase arriving under a fifth would be a
 *                 silent zero if the map were narrowed here, so it is not.
 *   readAt      — when this whole set was read, so a stale answer is visibly
 *                 stale rather than quietly current
 * Excluded:
 *   spend — `spendMinor` is already a top-level field on this document, read
 *     by the console and the overview; a copy here would be a second answer to
 *     what the campaign cost. It is written in the SAME update as this map, so
 *     `readAt` dates it too.
 *   frequency, cpc, cpm, ctr — each is a ratio of two figures above, and Meta
 *     computes them from the same numbers this map already carries
 */
export const CampaignAttributedSchema = z.object({
	reach: Count,
	impressions: Count,
	clicks: Count,
	actions: z
		.record(z.string(), Count)
		.describe(
			"what Meta counted as having happened, under Meta's own action-type keys — stored as returned, never narrowed to the tokens this product happens to name",
		),
	readAt: z.custom<Timestamp>(),
});
export type CampaignAttributed = z.infer<typeof CampaignAttributedSchema>;

/**
 * What THIS PRODUCT watched happen, joined to this campaign through the five
 * UTM keys its creative carries — the honest half of the pair.
 *
 * Derived fresh from live buyers, checkouts and orders on every poll rather
 * than accumulated, which is what makes a refund walk back on its own: a
 * refunded order's own document is what the sum reads, so the figure the
 * operator is shown is never inflated by money that went back. An accumulated
 * counter would need a second writer on the refund path, free to disagree with
 * the orders it was counting.
 *
 * Fields justified:
 *   visitors    — distinct buyers whose IMMUTABLE FIRST TOUCH names this
 *                 campaign. The traffic verdict reads it against
 *                 `attributed.clicks`, and it is the only member here that
 *                 measures arrival rather than intent, which is what lets a
 *                 broken join be told apart from a campaign nobody bought
 *                 from: a real campaign can send five hundred people and sell
 *                 nothing, so zero orders is a result and zero visitors
 *                 beneath Meta's own click count is a defect.
 *   checkouts   — baskets opened by those buyers
 *   orders      — paid orders among them
 *   ticketsSold — the figure cost-per-ticket-sold divides by; an order is any
 *                 number of tickets, so the order count cannot stand in
 *   revenueCents— what those orders are worth now, refunds already subtracted,
 *                 in euro cents (this product's own unit — never the ad
 *                 account's, which `currency` names and which the spend counts
 *                 in)
 *   readAt      — separate from `attributed.readAt` on purpose: a throttled
 *                 ad account leaves Meta's half stale while this half is
 *                 current, and one stamp over both would date the fresh half
 *                 by the stale one
 * Excluded:
 *   visits — a visit is a run of activity under a chosen gap, and the report
 *     callable already answers it under the gap the operator picked
 *     (`analyticsReport.action.ts`). A second, un-knobbed visit count here
 *     would be a different answer to the same question, differing only because
 *     nobody chose its gap.
 */
export const CampaignObservedSchema = z.object({
	visitors: Count,
	checkouts: Count,
	orders: Count,
	ticketsSold: Count,
	revenueCents: Cents,
	readAt: z.custom<Timestamp>(),
});
export type CampaignObserved = z.infer<typeof CampaignObservedSchema>;

/**
 * Whether the join that makes this whole feature measurable is actually
 * working — the one verdict that separates a campaign nobody responded to
 * from a campaign whose responses this product cannot see.
 *
 * The two look identical on a panel and only one of them is a bug. The ad
 * creative carries `utm_campaign={{campaign.id}}`, which Meta substitutes at
 * delivery time; if that macro is ever wrong, dropped, or stripped, arrivals
 * land carrying no id this product recognises, every figure in `observed`
 * reads zero, and the panel shows exactly what a campaign that sold nothing
 * shows. Meta's own click count is the independent origin that tells them
 * apart, which is why `attributed.clicks` is stored at all.
 *
 *   unreported  — Meta has not answered figures yet. Nothing to compare, and
 *                 saying so is not the same as saying nothing happened.
 *   none        — Meta reports no clicks. The ad has sent nobody here yet, so
 *                 an empty join is the correct and expected reading.
 *   arriving    — Meta reports clicks and buyers carrying this campaign's id
 *                 have arrived. The join works, whatever they went on to do.
 *   untraceable — Meta reports clicks and NOT ONE arrival carries this
 *                 campaign's id. Money is being spent on traffic this product
 *                 cannot attribute; every figure derived from the join is
 *                 meaningless rather than zero.
 */
export const CampaignTrafficSchema = z.enum([
	"unreported",
	"none",
	"arriving",
	"untraceable",
]);
export type CampaignTraffic = z.infer<typeof CampaignTrafficSchema>;

/**
 * The verdict above, derived rather than stored — one function, so the
 * poller's own alert and the console's badge cannot answer differently about
 * one campaign.
 */
export function campaignTraffic(campaign: {
	// Both `| undefined` explicitly, not merely optional: consumers compile
	// under `exactOptionalPropertyTypes`, where an optional property does not
	// admit an explicit `undefined` and a parsed nullish field carries one.
	readonly attributed?: CampaignAttributed | null | undefined;
	readonly observed?: CampaignObserved | null | undefined;
}): CampaignTraffic {
	if (campaign.attributed == null) return "unreported";
	if (campaign.attributed.clicks === 0) return "none";
	return (campaign.observed?.visitors ?? 0) > 0 ? "arriving" : "untraceable";
}

// events/{eventId}/campaigns/{campaignId}
// Fields justified — each field's own `.describe()` states what it IS; these
// name the reader that would notice it missing, where that is not obvious:
//   workspaceId       — the workspace overview's collection-group rule, the
//                       one read that resolves no path segment
//   status            — both schedules select on it; every console surface
//                       switches its whole face on it
//   priceFloorCents   — the confirmation, which refuses a proposal whose
//                       floor has moved since the copy was composed
//   failureReason     — the console, which owes the operator whose money did
//                       not get spent the one thing they can act on
//   claimedBy/claimedAt — the reconciler, which retakes a stale claim
//   stopRequestedAt   — the build's own activation gate, which must not turn
//                       an ad back on in the interval between an operator's
//                       press and the ad-level write that answers it
//   attributionWindow — the panel, so a spend figure is never shown without
//                       the terms it was read under
//   attributed/observed — the panel's two numbers, and together the traffic
//                       verdict (`campaignTraffic`) that separates a campaign
//                       nobody responded to from one whose responses this
//                       product cannot see
// Excluded:
//   eventId — the document sits under the event it promotes; a field beside
//     it would be a second answer, free to disagree with the path it was read at
//   campaignId — the doc id already carries it
//   a spend ceiling — the bound lives at the workspace's own
//     settings/adSpendCeiling singleton (ad-spend-ceiling.entity.ts), where
//     one figure bounds every campaign; a copy here would be a per-doc
//     bound a client that may edit budgetMinor could raise
//   the creative bytes, their geometry, and Meta's hash for them — one
//     creative runs, `proposal.assetId` names the document carrying all
//     three (creative-asset.entity.ts), and the build reads that document on
//     every path it takes before it would need the hash. A copy here would
//     be a second answer to what Meta calls the bytes, keyed to an assetId
//     it does not itself restate and therefore free to survive a change of
//     it — and the hash is a fact about the bytes, unlike `metaIds`, which
//     names objects this build minted and belongs on this document
export const CampaignSchema = z
	.object({
		workspaceId: Id.nullish().describe(
			"the workspace this campaign's event belongs to — absent until a server entry stamps it from the path the campaign is stored under",
		),
		status: CampaignStatusSchema.default("draft"),
		brief: CampaignBriefSchema.default({}),
		proposal: CampaignProposalSchema.nullish().describe(
			"what the composition came back with, and what the operator edits while it is parked — absent until a proposal exists",
		),
		proposedAt: z
			.custom<Timestamp>()
			.nullish()
			.describe(
				"when the composition parked — a proposal is a claim about prices, days and remaining quota that go stale, and the confirmation refuses one the world has moved under",
			),
		priceFloorCents: Cents.nullish().describe(
			"the cheapest ticket the public could buy at compose time, in euro cents — ticket-type.entity.ts's resolvePriceFloor, at quantity one. The copy splices this as literal text, so a floor that has since moved means the words are already wrong and the confirmation refuses the launch",
		),
		budgetMinor: MinorUnits.nullish().describe(
			"the lifetime budget the ad set carries — a lifetime budget, never a daily one, because a daily budget is not a cap and this product tells an operator what a campaign costs",
		),
		currency: z
			.string()
			.length(3)
			.nullish()
			.describe(
				"ISO 4217, read from the ad account — the unit budgetMinor and spendMinor count in",
			),
		endsAt: z
			.custom<Timestamp>()
			.nullish()
			.describe(
				"the last instant the public can still buy a ticket to this event — ticket-type.entity.ts's resolvePublicTicketSalesEnd, which reads no instant, so a tier still weeks from opening bounds the run exactly as one on sale now does. Derived, and mandatory beside a lifetime budget",
			),
		payor: z
			.string()
			.nullish()
			.describe("who pays for this ad, published in Meta's Ad Library (DSA)"),
		beneficiary: z
			.string()
			.nullish()
			.describe("who the ad is run for, published in Meta's Ad Library (DSA)"),
		// Snapshots of what WorkspaceSettings.metaAds held at the confirmation,
		// drawn from that shape (workspace.entity.ts's MetaAdvertiserSchema)
		// rather than restated here. Its looseness is the point on this
		// document above all: the format is policed where the operator's value
		// enters, so a vocabulary change at Meta can never make a campaign
		// carrying live spend unreadable.
		adAccountId: MetaAdvertiserSchema.shape.adAccountId.describe(
			"the ad account this campaign was built in, snapshotted at the confirmation",
		),
		pageId: MetaAdvertiserSchema.shape.pageId.describe(
			"the ad identity this campaign runs under, snapshotted at the confirmation",
		),
		metaIds: CampaignMetaIdsSchema.nullish(),
		metaStatus: z
			.string()
			.nullish()
			.describe(
				"how Meta itself currently describes this campaign's delivery, in Meta's own vocabulary rather than ours",
			),
		reviewFeedback: z
			.string()
			.nullish()
			.describe(
				"Meta's own review words when an ad is rejected — shown verbatim and never translated by us",
			),
		learningStage: z
			.string()
			.nullish()
			.describe(
				"Meta's learning_stage_info — whether delivery can optimise at all, visible rather than silent",
			),
		// Ours, and the only field on this document that says why. The refine
		// below deliberately does not require it beside `status: 'failed'`: a
		// campaign that failed carrying no reason is still a campaign the
		// console can render, and a refine would turn that into a whole-document
		// parse failure on the money path — the console's own generic panel is
		// what absence degrades to, and `unclassified` is what a reason nobody
		// could name resolves to, so the two never collide.
		failureReason: CampaignFailureReasonSchema.nullish().describe(
			"why this campaign's build did not produce a delivering ad, classified into the one closed set every remedy is keyed on — never a raw message from Meta or from us",
		),
		claimedBy: Id.nullish().describe(
			"the build lease holder — the delivery that won the transactional claim",
		),
		claimedAt: z
			.custom<Timestamp>()
			.nullish()
			.describe(
				"when the lease was taken; a stale one is a stalled build the reconciler retakes",
			),
		launchedBy: Id.nullish().describe("the member who committed the money"),
		launchedAt: z
			.custom<Timestamp>()
			.nullish()
			.describe("when they committed it"),
		// The press, recorded before ./stop.api.ts reaches Meta at all, where
		// `stoppedAt` below records the stop that landed. A build still holding
		// this tree reads THIS field and never the status, because the status
		// cannot move until the ad-level write has already landed.
		stopRequestedAt: z
			.custom<Timestamp>()
			.nullish()
			.describe(
				"when an operator last asked for this campaign's spend to stop — the request, not its outcome",
			),
		stoppedAt: z
			.custom<Timestamp>()
			.nullish()
			.describe("when the spend was stopped"),
		spendMinor: MinorUnits.nullish().describe(
			"what this campaign has cost so far, from Insights, in the ad account's own minor unit",
		),
		attributionWindow: z
			.string()
			.nullish()
			.describe(
				"the window spendMinor was read under, in Meta's own vocabulary — a spend figure is never quoted without the terms it holds under",
			),
		attributed: CampaignAttributedSchema.nullish().describe(
			"what Meta says this campaign delivered, written in the same update as spendMinor and the window that qualifies it — absent until the poller has read Insights once",
		),
		observed: CampaignObservedSchema.nullish().describe(
			"what this product itself watched happen, joined to this campaign through the UTM keys its creative carries — absent until the poller has run once",
		),
	})
	// A lifetime budget with no unit is not an amount, and Meta refuses a
	// lifetime budget with no end_time — so the three land together or not at
	// all, rather than each being separately nullish and free to arrive alone.
	.superRefine((campaign, ctx) => {
		if (campaign.budgetMinor != null && campaign.currency == null)
			ctx.addIssue({
				code: "custom",
				path: ["currency"],
				message: "a budget states the unit it counts in",
			});
		if (campaign.budgetMinor != null && campaign.endsAt == null)
			ctx.addIssue({
				code: "custom",
				path: ["endsAt"],
				message: "a lifetime budget states the instant it stops",
			});
		// From `proposed` on, the composition has run and its answer is what
		// the operator is being asked to confirm — so a document in one of
		// those states without one is not a campaign anyone could act on.
		if (
			campaign.proposal == null &&
			!PRE_PROPOSAL_STATUSES.includes(campaign.status)
		)
			ctx.addIssue({
				code: "custom",
				path: ["proposal"],
				message:
					"a campaign past drafting carries the proposal it was composed into",
			});
		// Money is committed at the confirmation, so every state that follows
		// it has an amount, and the two audit facts the confirmation stamps.
		if (
			campaign.budgetMinor == null &&
			LAUNCHED_STATUSES.includes(campaign.status)
		)
			ctx.addIssue({
				code: "custom",
				path: ["budgetMinor"],
				message:
					"a campaign that has reached the build carries the budget it was launched with",
			});
		if (
			campaign.launchedBy == null &&
			LAUNCHED_STATUSES.includes(campaign.status)
		)
			ctx.addIssue({
				code: "custom",
				path: ["launchedBy"],
				message:
					"money moves only from a recorded confirmation, so the member who gave it is on the document",
			});
		// A proposal is a claim about prices and quota that go stale, so the two
		// facts the confirmation measures that claim against travel with it —
		// absent, there is nothing to compare and the staleness check passes by
		// having no world to check against.
		for (const [field, message] of [
			[
				"proposedAt",
				"a parked proposal carries the instant it parked, or nothing can tell whether it still holds",
			],
			[
				"priceFloorCents",
				"a parked proposal carries the floor its own words quote, or nothing can tell whether they are still true",
			],
		] as const)
			if (
				campaign[field] == null &&
				!PRE_PROPOSAL_STATUSES.includes(campaign.status)
			)
				ctx.addIssue({ code: "custom", path: [field], message });
		// Snapshotted at the confirmation, and each refused as absent by the
		// build: Meta rejects an ad set carrying no DSA pair outright, and a
		// campaign that cannot name the account and identity it was built under
		// is one nothing can reconcile against Meta afterwards.
		for (const [field, message] of [
			["payor", "a published ad names who paid for it"],
			["beneficiary", "a published ad names who it was run for"],
			[
				"adAccountId",
				"a built campaign carries the ad account it was built in",
			],
			["pageId", "a built campaign carries the identity it runs under"],
		] as const)
			if (
				campaign[field] == null &&
				LAUNCHED_STATUSES.includes(campaign.status)
			)
				ctx.addIssue({ code: "custom", path: [field], message });
	});
// The refine above makes this a `ZodEffects`, which in zod 3 carries neither
// `.pick` nor `.shape`. A consumer projecting a patch takes it at the TYPE
// level — `Pick<Campaign, 'status' | 'metaStatus'>` — which tsc re-checks
// against these fields on every reshape. No base object is exported to pick
// from at runtime: the only thing such an export would buy a caller over
// `Pick` is a parse that skips every guarantee the refine states, on the one
// path where money has already moved.
export type Campaign = z.infer<typeof CampaignSchema>;
// `status` and `brief` carry defaults, so a writer that lets CampaignSchema
// fill either in types its own literal against this input shape.
export type CampaignInput = z.input<typeof CampaignSchema>;

export const Campaign = entity<Actor>()({
	schema: CampaignSchema,
	actions: ["read", "list", "create", "update", "launch"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		// The event console's own campaign surface: one document, and the list
		// under this one event. Both on the same predicate, declared separately
		// for the reason the file header gives.
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.ads.manage", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.ads.manage", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		// Birth: a draft carrying a brief and nothing else. `.set(...)` emits
		// value equalities and NO key mask, so every field the server owns is
		// pinned to its birth value here — the whole defence, and the reason
		// the list reads long. A campaign born past `draft` would reach the
		// build trigger without a proposal ever having been composed or
		// confirmed; a campaign born carrying `metaIds` would send the build's
		// own hydrate-and-skip path at an ad id the operator chose; and a
		// campaign born naming its own `workspaceId` could name one its path
		// does not, which the overview's own binding below would honour.
		can("create")
			.when((_data, me) =>
				and(
					hasPermission(me.permissions, "tickets.ads.manage", {
						event: pathVar("eventId"),
					}),
					me.mfa.eq(true),
				),
			)
			.writes((w) =>
				w.set({
					status: "draft",
					workspaceId: null,
					proposal: null,
					proposedAt: null,
					priceFloorCents: null,
					budgetMinor: null,
					currency: null,
					endsAt: null,
					payor: null,
					beneficiary: null,
					adAccountId: null,
					pageId: null,
					metaIds: null,
					metaStatus: null,
					reviewFeedback: null,
					learningStage: null,
					failureReason: null,
					claimedBy: null,
					claimedAt: null,
					launchedBy: null,
					launchedAt: null,
					stopRequestedAt: null,
					stoppedAt: null,
					spendMinor: null,
					attributionWindow: null,
					attributed: null,
					observed: null,
				}),
			),
		// Mask one: the brief, while nothing has been composed yet. The state
		// lives in `.from(...)` and nowhere else — see the file-level note on
		// why no update cond names it.
		can("update")
			.when((_data, me) =>
				and(
					hasPermission(me.permissions, "tickets.ads.manage", {
						event: pathVar("eventId"),
					}),
					me.mfa.eq(true),
				),
			)
			.writes((w) => w.updateMask(["brief"]).from((d) => d.status.eq("draft"))),
		// Mask two: the parked proposal and its amount. The five controls write
		// into `proposal`; the amount is its own top-level field, so the mask
		// can name it and the ceiling check at the confirmation has one figure
		// to read.
		can("update")
			.when((_data, me) =>
				and(
					hasPermission(me.permissions, "tickets.ads.manage", {
						event: pathVar("eventId"),
					}),
					me.mfa.eq(true),
				),
			)
			.writes((w) =>
				w
					.updateMask(["proposal", "budgetMinor"])
					.from((d) => d.status.eq("proposed")),
			),
		// The confirmation — the same authority the FE gate and the callable
		// both read, with no emitted allow line beside it. See the file-level
		// note on why `proposed → building` has exactly one path.
		can("launch")
			.when((data, me) =>
				and(
					hasPermission(me.permissions, "tickets.ads.manage", {
						event: pathVar("eventId"),
					}),
					me.mfa.eq(true),
					data.status.eq("proposed"),
				),
			)
			.requiresHandler("launchCampaign"),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/campaigns/{campaignId}",
		}),
	);

/**
 * WorkspaceCampaigns — the same documents, read across every event at once.
 *
 * The workspace overview ranks a workspace's promotions against each other,
 * so it is a `collectionGroup('campaigns')` query rather than one
 * subcollection read per event. A collection-group query binds no path
 * segment, so `Campaign`'s own rule — whose every check resolves `{orgId}`
 * from the path — cannot answer it at all. This second binding is that
 * query's own rule, keyed on the one tenant fact the document itself carries.
 *
 * It is NOT scoped to that query. The recursive wildcard matches zero or more
 * segments, so this block also matches a campaign at its real nested path, and
 * Firestore ORs every applicable allow expression: the per-event list is
 * governed by the path-keyed grant OR this field-keyed one. That is safe only
 * because no client can author the field — see `Campaign`'s own note.
 *
 * Two obligations ride with it. The emitted rule reads
 * `resource.data.workspaceId`, which Firestore resolves ONLY from the query's
 * own equality constraint, so the overview query must carry
 * `where('workspaceId', '==', orgId)` — without it the attempt is the bare
 * cross-tenant enumeration and the rule refuses it, which is the isolation
 * rather than a bug. And that same field needs an explicit single-field index
 * (`firestore.indexes.json`'s `fieldOverrides`), because a field override
 * replaces the automatic indexing for that field rather than adding to it.
 *
 * Only `list` is declared. A single document is read at its real path through
 * `Campaign` above, and a `read` here would OR a second, doc-keyed grant past
 * the path-keyed one for every `get` in the workspace.
 *
 * The plural is deliberate and is the only one in this package: every sibling
 * entity names a document kind, and this one names no kind at all — it is the
 * same document `Campaign` already names, bound a second time for a query that
 * spans many. A singular name here would assert a second kind of campaign the
 * model does not have.
 */
export const WorkspaceCampaigns = entity<Actor>()({
	schema: CampaignSchema,
	actions: ["list"] as const,
})
	.pipe(({ can, hasPermission, and }) => [
		can("list").when((_data, me) =>
			and(hasPermission(me.permissions, "tickets.ads.manage"), me.mfa.eq(true)),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "{path=**}/campaigns/{campaignId}",
		}),
	);
